import { useEffect, useMemo, useRef, useState } from "react";

interface Props {
  /** ファイル名 → ソース */
  files: Record<string, string>;
  /** 編集中のファイル名 */
  activeFile: string;
  /** スコープ初期値 */
  initialQuery?: string;
  onClose: () => void;
  /** 1 ファイル分を置換した結果を反映する */
  onReplace: (filename: string, newSource: string) => void;
  /** ファイルを切り替える(結果クリックでジャンプ) */
  onSelectFile: (filename: string, line: number) => void;
}

interface Hit {
  file: string;
  line: number;
  col: number;
  before: string;
  match: string;
  after: string;
}

function buildRegex(
  q: string,
  opts: { caseSensitive: boolean; whole: boolean; regex: boolean },
): RegExp | null {
  if (!q) return null;
  let pattern = opts.regex ? q : escapeRegex(q);
  if (opts.whole) pattern = `\\b${pattern}\\b`;
  const flags = "g" + (opts.caseSensitive ? "" : "i");
  try {
    return new RegExp(pattern, flags);
  } catch {
    return null;
  }
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const MAX_HITS_PER_FILE = 200;

export function FindReplaceModal({
  files,
  activeFile,
  initialQuery = "",
  onClose,
  onReplace,
  onSelectFile,
}: Props) {
  const [query, setQuery] = useState(initialQuery);
  const [replaceVal, setReplaceVal] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [whole, setWhole] = useState(false);
  const [regex, setRegex] = useState(false);
  const [scope, setScope] = useState<"current" | "all">("all");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const re = useMemo(
    () => buildRegex(query, { caseSensitive, whole, regex }),
    [query, caseSensitive, whole, regex],
  );

  const hits: Hit[] = useMemo(() => {
    if (!re) return [];
    const out: Hit[] = [];
    const targets =
      scope === "current" && files[activeFile] !== undefined
        ? { [activeFile]: files[activeFile]! }
        : files;
    for (const [file, source] of Object.entries(targets)) {
      const lines = source.split(/\r?\n/);
      let perFile = 0;
      for (let i = 0; i < lines.length && perFile < MAX_HITS_PER_FILE; i++) {
        const line = lines[i]!;
        const lineRe = new RegExp(re.source, re.flags);
        let m: RegExpExecArray | null;
        while ((m = lineRe.exec(line)) !== null) {
          out.push({
            file,
            line: i + 1,
            col: m.index + 1,
            before: line.slice(Math.max(0, m.index - 20), m.index),
            match: m[0],
            after: line.slice(m.index + m[0].length, m.index + m[0].length + 30),
          });
          perFile++;
          if (m[0].length === 0) lineRe.lastIndex++;
          if (perFile >= MAX_HITS_PER_FILE) break;
        }
      }
    }
    return out;
  }, [re, files, scope, activeFile]);

  const fileGroups = useMemo(() => {
    const g: Record<string, Hit[]> = {};
    for (const h of hits) {
      (g[h.file] ??= []).push(h);
    }
    return g;
  }, [hits]);

  function replaceAll() {
    if (!re) return;
    const useFiles =
      scope === "current" && files[activeFile] !== undefined
        ? { [activeFile]: files[activeFile]! }
        : files;
    let total = 0;
    for (const [file, source] of Object.entries(useFiles)) {
      const r = new RegExp(re.source, re.flags);
      let count = 0;
      const next = source.replace(r, () => {
        count++;
        return regex ? expandReplacement(replaceVal) : replaceVal;
      });
      if (count > 0) {
        onReplace(file, next);
        total += count;
      }
    }
    alert(`${total} 件を置換しました`);
  }

  function expandReplacement(template: string): string {
    // 正規表現モードでは $1 等の後方参照を有効化(JS String.replace の標準動作で OK)
    return template;
  }

  const totalHits = hits.length;
  const fileCount = Object.keys(fileGroups).length;

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal modal-find"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>検索と置換</h2>

        <div className="find-row">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="検索文字列(または正規表現)"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                // 最初のヒットへジャンプ
                if (hits[0]) onSelectFile(hits[0].file, hits[0].line);
              }
            }}
          />
        </div>
        <div className="find-row">
          <input
            type="text"
            value={replaceVal}
            onChange={(e) => setReplaceVal(e.target.value)}
            placeholder="置換後(空でも可)"
          />
        </div>

        <div className="find-opts">
          <label>
            <input
              type="checkbox"
              checked={caseSensitive}
              onChange={(e) => setCaseSensitive(e.target.checked)}
            />
            大小区別
          </label>
          <label>
            <input
              type="checkbox"
              checked={whole}
              onChange={(e) => setWhole(e.target.checked)}
            />
            単語単位
          </label>
          <label>
            <input
              type="checkbox"
              checked={regex}
              onChange={(e) => setRegex(e.target.checked)}
            />
            正規表現
          </label>
          <span className="find-spacer" />
          <label>
            <input
              type="radio"
              checked={scope === "all"}
              onChange={() => setScope("all")}
            />
            全ファイル
          </label>
          <label>
            <input
              type="radio"
              checked={scope === "current"}
              onChange={() => setScope("current")}
            />
            現在のファイル
          </label>
        </div>

        <div className="find-stats">
          {query ? (
            re ? (
              <span>
                {totalHits} 件 / {fileCount} ファイル{" "}
                {hits.length >= MAX_HITS_PER_FILE * fileCount && (
                  <span style={{ color: "var(--warn, #ff8888)" }}>
                    (上限到達 {MAX_HITS_PER_FILE}/file)
                  </span>
                )}
              </span>
            ) : (
              <span style={{ color: "#ff8888" }}>正規表現が無効です</span>
            )
          ) : (
            <span style={{ color: "#557799" }}>クエリを入力してください</span>
          )}
        </div>

        <div className="find-results">
          {Object.entries(fileGroups).map(([file, hs]) => (
            <div key={file} className="find-file-group">
              <div className="find-file-label">
                {file} <span className="find-file-count">({hs.length})</span>
              </div>
              {hs.slice(0, 30).map((h, i) => (
                <button
                  key={i}
                  className="find-hit"
                  onClick={() => onSelectFile(h.file, h.line)}
                  title={`${h.file}:${h.line}:${h.col}`}
                >
                  <span className="find-line">L{h.line}</span>
                  <span className="find-text">
                    {h.before}
                    <mark>{h.match}</mark>
                    {h.after}
                  </span>
                </button>
              ))}
              {hs.length > 30 && (
                <div className="find-more">
                  …他 {hs.length - 30} 件
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>
            閉じる
          </button>
          <button
            className="btn btn-warn"
            onClick={replaceAll}
            disabled={!re || totalHits === 0}
          >
            すべて置換 ({totalHits})
          </button>
        </div>
      </div>
    </div>
  );
}
